/**
 * The device-subject label — the WEB-side single owner (Story 8.5, AD-25).
 *
 * Story 7.1 wrote the model+revision concatenation INLINE, twice: once in
 * `SiteDetail`'s install picker and once in `RequestsInboxView`'s merge-target
 * `<option>` — whose comment already said it was copying the other. Meanwhile the
 * three certificate-subject pickers (declare / create / request) rendered a
 * server-baked `label` that was the bare model, so the actor asserting what a
 * certificate covers could not tell two revisions of one model apart. This module is
 * the one place the web composes that label; `device-label.guard.test.ts` is what
 * makes "one place" true, by SCANNING THE SOURCES rather than trusting this comment.
 *
 * **Its server twin is `packages/domain/src/device-label.ts`, and the two cannot be
 * one module.** `@mvcp/domain` is deliberately NOT a dependency of `apps/web`
 * (AD-8) — the web consumes `@mvcp/contracts` as *types only* and a runtime import
 * from either package breaks `vite build` — and the request email body and the PDF
 * are composed on the server where no web module can reach. Same rule, two owners,
 * each guarded; AD-25 records the split so the next surface finds it stated rather
 * than discovering it as an inconsistency. Two owners is the limit: a third copy is
 * what the guard exists to fail.
 *
 * This module also owns the web's **device revision vocabulary**, not just the label:
 * {@link deviceRevision} (a revision as a comparable value) and
 * {@link mergeDestroysRevision} (AD-24 clause 5's distinguishing test). Story 8.5's
 * first pass declared both inline in `RequestsInboxView`, which was a THIRD copy of the
 * same rule in the same story that created the owner — so they live here, and the guard
 * scan fails a re-declaration.
 */

/** The minimum a device must carry to be named. `version` may be absent, `null` or blank. */
export interface DeviceSubjectLike {
  model: string;
  version?: string | null;
}

/**
 * Compose the human label for a device subject: `model`, then the revision appended
 * after a single space **only** when the device carries a non-empty one.
 *
 * An absent / `null` / whitespace-only revision yields the bare model — never a
 * trailing space, never the literal `undefined`/`null`, never an empty parenthetical.
 * An EMPTY part is dropped rather than joined, so a row whose `model` is empty or
 * whitespace-only yields the revision alone rather than a label with a LEADING space.
 * Identical rule and identical output to the server composer (pinned on both sides, and
 * against one shared case table — `test-fixtures/device-label-cases.json`).
 */
export function deviceLabel(device: DeviceSubjectLike): string {
  const model = (device.model ?? '').trim();
  const version = (device.version ?? '').trim();
  if (!model) return version;
  return version ? `${model} ${version}` : model;
}

/**
 * A device's revision as a COMPARABLE value: trimmed, with absent / `null` reading as
 * the empty string. The one place the web decides what "this device's revision" is, so
 * the label above and the distinguishing test below cannot disagree about whether
 * `'  '` counts as a revision (it does not).
 */
export function deviceRevision(device: { version?: string | null }): string {
  return (device.version ?? '').trim();
}

/**
 * Would merging `loser` into `winner` DESTROY a distinguishing revision (Story 8.5,
 * AD-25 clause 7 / AD-24 clause 5)? True when the loser carries a non-empty revision
 * that is not case-insensitively equal to the winner's — `R2` and `r2` are one value
 * spelled two ways — including the case where the winner records none at all. A
 * version-less loser has nothing to lose.
 *
 * **This is the AFFORDANCE, not the guarantee.** The server refuses the same case with a
 * 409 and is authoritative for every caller, including any that never opens the confirm
 * modal (`admin-requests.repository.ts` `mergeDevice`). The web needs its own copy of the
 * predicate only so the administrator is asked BEFORE a request is sent (AD-24 clause 4 —
 * a loser is disclosed before commit), which a 409 arriving after the click cannot do.
 * That is the same two-owners-per-side split AD-25 records for the label itself, which is
 * exactly why the predicate belongs HERE rather than in the one view that happens to
 * need it today.
 */
export function mergeDestroysRevision(
  loser: { version?: string | null },
  winner: { version?: string | null },
): boolean {
  const lost = deviceRevision(loser);
  return lost !== '' && lost.toLowerCase() !== deviceRevision(winner).toLowerCase();
}
